import React, { createContext, useContext, useEffect, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const VISITOR_KEY = "psytech_visitor_id";
const SESSION_KEY = "psytech_session_id";
const SESSION_TS_KEY = "psytech_session_ts";
const SESSION_TIMEOUT_MS = 30 * 60 * 1000; // 30 min

const TrackerContext = createContext({ track: () => {} });

function uuid() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  return "x".repeat(8).replace(/x/g, () =>
    Math.floor(Math.random() * 16).toString(16)
  ) + "-" + Date.now().toString(16);
}

function getOrInitVisitor() {
  try {
    let vid = localStorage.getItem(VISITOR_KEY);
    if (!vid) {
      vid = uuid();
      localStorage.setItem(VISITOR_KEY, vid);
    }
    let sid = sessionStorage.getItem(SESSION_KEY);
    const ts = parseInt(sessionStorage.getItem(SESSION_TS_KEY) || "0", 10);
    const expired = !ts || Date.now() - ts > SESSION_TIMEOUT_MS;
    if (!sid || expired) {
      sid = uuid();
      sessionStorage.setItem(SESSION_KEY, sid);
    }
    sessionStorage.setItem(SESSION_TS_KEY, String(Date.now()));
    return { visitor_id: vid, session_id: sid };
  } catch {
    return { visitor_id: uuid(), session_id: uuid() };
  }
}

function detectDevice() {
  const ua = navigator.userAgent || "";
  if (/Mobi|Android|iPhone/i.test(ua)) return "mobile";
  if (/iPad|Tablet/i.test(ua)) return "tablet";
  return "desktop";
}

function detectBrowser() {
  const ua = navigator.userAgent || "";
  if (/Edg\//.test(ua)) return "Edge";
  if (/Chrome\//.test(ua) && !/Edg\//.test(ua)) return "Chrome";
  if (/Firefox\//.test(ua)) return "Firefox";
  if (/Safari\//.test(ua) && !/Chrome\//.test(ua)) return "Safari";
  return "Other";
}

function readUtm() {
  try {
    const sp = new URLSearchParams(window.location.search);
    return {
      utm_source: sp.get("utm_source") || undefined,
      utm_medium: sp.get("utm_medium") || undefined,
      utm_campaign: sp.get("utm_campaign") || undefined,
    };
  } catch {
    return {};
  }
}

export function TrackerProvider({ children }) {
  const location = useLocation();
  const ids = useRef(getOrInitVisitor());

  const track = useCallback(async (event_type, props = {}) => {
    const path = window.location.pathname;
    // Don't track admin pages — they're not for analytics
    if (path.startsWith("/admin")) return;
    const payload = {
      ...ids.current,
      event_type,
      page_path: path,
      page_title: document.title,
      referrer: document.referrer || undefined,
      device_type: detectDevice(),
      browser: detectBrowser(),
      language: (document.documentElement.lang || "en").slice(0, 8),
      ...readUtm(),
      ...props,
    };
    try {
      await axios.post(`${API}/track`, payload);
    } catch {
      /* swallow — analytics must never break UX */
    }
  }, []);

  // Auto page_view on each route change
  useEffect(() => {
    if (location.pathname.startsWith("/admin")) return;
    track("page_view");
  }, [location.pathname, track]);

  // Expose globally for non-React callers (e.g. analytics-instrumented assistant widget mounted at root)
  useEffect(() => {
    window.__psyTrack = track;
    return () => { delete window.__psyTrack; };
  }, [track]);

  return (
    <TrackerContext.Provider value={{ track }}>
      {children}
    </TrackerContext.Provider>
  );
}

export function useTracker() {
  return useContext(TrackerContext);
}
